import { useState } from "react";
import { Box, Text, Stack, HStack, Image, Wrap, WrapItem, Button, UnorderedList, ListItem, Link, Icon } from "@chakra-ui/react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";
import { useExperience, useCategories, pick } from "../hooks/useContent";
import { experienceCategories, employmentTypeLabel } from "../admin/collectionsConfig";
import { formatDateRange } from "../utils/dateRange";
import { experienceBullets } from "../utils/bullets";
import { ListCardsSkeleton } from "./ui/Skeletons";
import Section from "./ui/Section";

// Asegura que la URL tenga protocolo; si no, el enlace sería relativo al sitio.
const withProtocol = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

function Experience() {
  const { language } = useLanguage();
  const { data: experience = [], loading } = useExperience();
  const { data: dbCats } = useCategories("experience");
  const [filter, setFilter] = useState("all");

  const eyebrow = language === "es" ? "TRAYECTORIA" : "CAREER";
  const title = language === "es" ? "Experiencia" : "Experience";
  const catLabel = (c) => (language === "es" ? c.label_es : c.label_en);

  // Use editable categories from the DB; fall back to the static list.
  const categories = dbCats.length ? dbCats : experienceCategories;
  // Only show filter chips for categories that actually have entries.
  const presentCategories = categories.filter((c) =>
    experience.some((e) => e.category === c.value)
  );

  const filtered =
    filter === "all"
      ? experience
      : experience.filter((e) => e.category === filter);

  if (loading) {
    return (
      <Section id="experience" eyebrow={eyebrow} title={title}>
        <ListCardsSkeleton count={3} />
      </Section>
    );
  }

  return (
    <Section id="experience" eyebrow={eyebrow} title={title}>
      {presentCategories.length > 0 && (
        <Wrap spacing={2} mb={6}>
          <WrapItem>
            <Button
              size="sm"
              variant={filter === "all" ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => setFilter("all")}
            >
              {language === "es" ? "Todos" : "All"}
            </Button>
          </WrapItem>
          {presentCategories.map((c) => (
            <WrapItem key={c.value}>
              <Button
                size="sm"
                variant={filter === c.value ? "solid" : "outline"}
                colorScheme="blue"
                onClick={() => setFilter(c.value)}
              >
                {catLabel(c)}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Stack spacing={4}>
        {filtered.map((e) => (
          <Box
            key={e.id}
            bg="bg.card"
            border="1px solid"
            borderColor="border.card"
            borderLeft="4px solid"
            borderLeftColor="brand.400"
            borderRadius="xl"
            p={6}
          >
            <HStack spacing={3} align="center">
              {e.image_url && (
                <Image
                  src={e.image_url}
                  alt={e.company}
                  boxSize="44px"
                  objectFit="contain"
                  borderRadius="md"
                  bg="white"
                  p={1}
                  flexShrink={0}
                />
              )}
              <Box>
                <Text fontWeight="bold" fontSize="lg" color="text.heading">
                  {pick("role", e, language)}
                </Text>
                <Text color="text.muted" fontSize="sm">
                  {e.url ? (
                    <Link
                      href={withProtocol(e.url)}
                      isExternal
                      color="brand.400"
                      fontWeight="medium"
                      textDecoration="underline"
                      _hover={{ color: "brand.300" }}
                    >
                      {e.company}
                      <Icon as={FaExternalLinkAlt} boxSize="0.7em" ml={1} verticalAlign="baseline" />
                    </Link>
                  ) : (
                    e.company
                  )}
                  {e.employment_type
                    ? ` · ${employmentTypeLabel(e.employment_type, language)}`
                    : ""}
                </Text>
              </Box>
            </HStack>
            <Text color="brand.400" fontSize="sm" mt={1}>
              {formatDateRange(e.date_start, e.date_end, e.current, language)}
              {e.location ? ` · ${e.location}` : ""}
            </Text>
            {(() => {
              const items = experienceBullets(e, language);
              return items.length > 0 ? (
                <UnorderedList color="text.muted" mt={3} fontSize="sm" spacing={1}>
                  {items.map((b, i) => (
                    <ListItem key={i}>{b}</ListItem>
                  ))}
                </UnorderedList>
              ) : null;
            })()}
          </Box>
        ))}
      </Stack>
    </Section>
  );
}

export default Experience;
